import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { WeeklyWorkTimeInterface } from '../../interfaces/weekly-work-time.interface';
import { WorkdayModel } from '../../models/low-level/workday.model';
import {
    selectCurrentDayDurationInMinutes,
    selectTotalWeekDurationInMinutes,
    selectWeeklyWorkdays,
} from '../../state/timesheet/selectors/weekly-work-time.selector';
import { weeklyWorkdaysToModels } from '../../state/timesheet/timesheet-collection.converters';
import { initConfirmations, selectConfirmationCollectionLoaded } from '../../state';

/**
 * NgRx-backed implementation of {@link WeeklyWorkTimeInterface}. Everything it reports is derived
 * from the timesheet slice — there is no separate weekly-work-time store slice, and neither
 * `init()` nor `syncWeeklyWorkTime()` has anything left to do.
 *
 * The legacy service kept a persisted copy of a derived number: the file/concurrent storage pair
 * and their timestamp merge, the backfill loop for missing days, the new-week reset, and the
 * per-submission duration update. None of that has an equivalent here — the timesheet slice already
 * holds and persists everything this reads, so the two methods below are genuinely empty. `init()`
 * is called by `InitializationService` after `timesheetAndExpensesService.init()`, so the timesheet
 * slice is already populated by the time anything reads these selectors.
 *
 * The selectors count minutes; {@link WeeklyWorkTimeInterface} reports hours, so the two duration
 * getters convert at this boundary.
 */
@Injectable({
    providedIn: 'root',
})
export class WeeklyWorkTimeNgrxService implements WeeklyWorkTimeInterface {
    private readonly store = inject(Store);

    private readonly totalWeekDurationInMinutes = this.store.selectSignal(selectTotalWeekDurationInMinutes);
    private readonly currentDayDurationInMinutes = this.store.selectSignal(selectCurrentDayDurationInMinutes);

    public readonly weeklyWorkTimeValue$: Observable<WorkdayModel[]> = this.store
        .select(selectWeeklyWorkdays)
        .pipe(map(weeklyWorkdaysToModels));

    public init(): Observable<unknown> {
        return this.store.select(selectConfirmationCollectionLoaded).pipe(
            tap((confirmationCollectionLoaded) => {
                if (!confirmationCollectionLoaded) {
                    this.store.dispatch(initConfirmations());
                }
            }),
        );
    }

    public syncWeeklyWorkTime(): Observable<void> {
        return of(undefined);
    }

    public getTotalWeekDuration(): number {
        return moment.duration(this.totalWeekDurationInMinutes(), 'minutes').asHours();
    }

    public getCurrentDayTotalDuration(): number {
        return moment.duration(this.currentDayDurationInMinutes(), 'minutes').asHours();
    }
}
